import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  ArrowRight,
  AlertTriangle,
  Phone,
  Wind,
  Heart,
  Brain,
  Activity,
  Smile,
  Search,
  ClipboardList,
  Eye,
  Stethoscope,
  RotateCcw,
} from 'lucide-react';
import SiteHeaderV2 from '@/components/website/SiteHeaderV2';
import SiteFooterV2 from '@/components/website/SiteFooterV2';
import PageTitle from '@/components/website/PageTitle';

/* eslint-disable react/no-unescaped-entities */

// Site palette (aligned with CabinetHomePage)
const C = {
  primary: '#0EA5E9',
  primaryDark: '#0284C7',
  ink: '#0F172A',
  slate: '#475569',
  muted: '#94A3B8',
  cloud: '#F8FAFC',
  skyPale: '#F0F9FF',
  skySoft: '#E0F2FE',
  border: '#E2E8F0',
  white: '#FFFFFF',
  danger: '#DC2626',
  dangerBg: '#FEF2F2',
  dangerBorder: '#FCA5A5',
  amber: '#D97706',
  amberBg: '#FFFBEB',
  amberBorder: '#FCD34D',
};

// --- Nijmegen questionnaire ------------------------------------------------
const NIJMEGEN_ITEMS = [
  'Douleur thoracique',
  'Sensation de tension',
  'Vision floue',
  'Vertiges',
  'Sensation de confusion',
  'Respiration rapide ou profonde',
  'Essoufflement',
  'Oppression thoracique',
  'Ventre gonflé',
  'Fourmillements dans les doigts',
  'Impossible de respirer à fond',
  'Doigts ou bras raides',
  'Tension autour de la bouche',
  'Mains ou pieds froids',
  'Palpitations',
  'Sensation d’anxiété',
];
const NIJMEGEN_LEVELS = [
  { value: 0, label: 'Jamais' },
  { value: 1, label: 'Rarement' },
  { value: 2, label: 'Parfois' },
  { value: 3, label: 'Souvent' },
  { value: 4, label: 'Très souvent' },
];

// --- Small building blocks -------------------------------------------------
const SectionLabel = ({ number, children }) => (
  <div className="flex items-center gap-3 mb-4">
    {number != null && (
      <span
        className="inline-flex items-center justify-center rounded-full text-xs font-semibold"
        style={{
          width: 28,
          height: 28,
          backgroundColor: C.skySoft,
          color: C.primaryDark,
          fontFamily: 'Figtree, sans-serif',
        }}
      >
        {number}
      </span>
    )}
    <span
      className="text-xs sm:text-sm uppercase tracking-[0.22em]"
      style={{ color: C.primaryDark, fontWeight: 500 }}
    >
      {children}
    </span>
  </div>
);

const SectionTitle = ({ children }) => (
  <h2
    className="text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6"
    style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: C.ink }}
  >
    {children}
  </h2>
);

const Callout = ({ variant = 'info', icon: Icon, title, children }) => {
  const styles =
    variant === 'danger'
      ? { bg: C.dangerBg, border: C.dangerBorder, color: C.danger }
      : variant === 'warning'
      ? { bg: C.amberBg, border: C.amberBorder, color: C.amber }
      : { bg: C.skyPale, border: C.skySoft, color: C.primaryDark };
  return (
    <div
      className="rounded-2xl p-5 sm:p-6 flex gap-4"
      style={{ backgroundColor: styles.bg, border: `1px solid ${styles.border}` }}
    >
      {Icon && (
        <div className="flex-shrink-0" style={{ color: styles.color }}>
          <Icon size={22} />
        </div>
      )}
      <div className="flex-1">
        {title && (
          <p
            className="mb-1 font-semibold"
            style={{ color: styles.color, fontFamily: 'Figtree, sans-serif' }}
          >
            {title}
          </p>
        )}
        <div style={{ color: C.ink, fontSize: '0.95rem', lineHeight: 1.6 }}>
          {children}
        </div>
      </div>
    </div>
  );
};

const SymptomGroup = ({ icon: Icon, title, items }) => (
  <div
    className="rounded-2xl p-6 h-full"
    style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
    data-testid={`symptom-group-${title}`}
  >
    <div className="flex items-center gap-3 mb-4">
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: C.skySoft, color: C.primaryDark }}
      >
        <Icon size={20} />
      </div>
      <h3
        className="text-lg font-medium"
        style={{ color: C.ink, fontFamily: 'Figtree, sans-serif' }}
      >
        {title}
      </h3>
    </div>
    <ul className="space-y-2">
      {items.map((s) => (
        <li key={s} className="flex gap-2 text-sm" style={{ color: C.slate }}>
          <span
            className="mt-2 rounded-full flex-shrink-0"
            style={{ width: 5, height: 5, backgroundColor: C.primary }}
          />
          <span>{s}</span>
        </li>
      ))}
    </ul>
  </div>
);

const StepCard = ({ number, title, children }) => (
  <div
    className="rounded-2xl p-6 h-full flex flex-col"
    style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
  >
    <div
      className="w-9 h-9 rounded-full flex items-center justify-center mb-4"
      style={{
        backgroundColor: C.primary,
        color: C.white,
        fontFamily: 'Outfit, sans-serif',
        fontWeight: 500,
      }}
    >
      {number}
    </div>
    <h4 className="text-base font-semibold mb-2" style={{ color: C.ink }}>
      {title}
    </h4>
    <div className="text-sm leading-relaxed" style={{ color: C.slate }}>
      {children}
    </div>
  </div>
);

const ExerciseCard = ({ index, title, duration, steps, closing }) => (
  <div
    className="rounded-2xl p-6 sm:p-7 h-full"
    style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
    data-testid={`exercise-card-${index}`}
  >
    <div className="flex items-center justify-between mb-4">
      <span
        className="text-xs uppercase tracking-[0.2em] font-medium"
        style={{ color: C.primaryDark }}
      >
        Exercice {index}
      </span>
      <span
        className="text-xs px-3 py-1 rounded-full"
        style={{ backgroundColor: C.skySoft, color: C.primaryDark }}
      >
        {duration}
      </span>
    </div>
    <h3
      className="text-xl mb-4"
      style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
    >
      {title}
    </h3>
    <ol className="space-y-3 mb-4">
      {steps.map((step, idx) => (
        <li key={idx} className="flex gap-3 text-sm" style={{ color: C.slate }}>
          <span
            className="flex-shrink-0 flex items-center justify-center text-xs font-semibold"
            style={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              backgroundColor: C.skyPale,
              color: C.primaryDark,
            }}
          >
            {idx + 1}
          </span>
          <span className="leading-relaxed">{step}</span>
        </li>
      ))}
    </ol>
    {closing && (
      <p
        className="text-sm italic pt-3"
        style={{ color: C.primaryDark, borderTop: `1px dashed ${C.border}` }}
      >
        {closing}
      </p>
    )}
  </div>
);

const FaqItem = ({ q, children, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 transition-colors"
        style={{ color: C.ink }}
        data-testid={`faq-toggle-${index}`}
      >
        <span className="font-medium" style={{ fontFamily: 'Figtree, sans-serif' }}>
          {q}
        </span>
        <ChevronRight
          size={18}
          style={{
            color: C.primary,
            transition: 'transform 0.25s ease',
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
          }}
        />
      </button>
      {open && (
        <div
          className="px-5 pb-5 text-sm leading-relaxed"
          style={{ color: C.slate, borderTop: `1px solid ${C.border}` }}
        >
          <div className="pt-4 space-y-3">{children}</div>
        </div>
      )}
    </div>
  );
};

// --- Main page -------------------------------------------------------------
const DysfunctionalBreathingPage = () => {
  const navigate = useNavigate();

  const [answers, setAnswers] = useState(Array(NIJMEGEN_ITEMS.length).fill(null));
  const answeredCount = answers.filter((v) => v !== null).length;
  const totalScore = useMemo(
    () => answers.reduce((sum, v) => sum + (v ?? 0), 0),
    [answers]
  );
  const highFreqCount = useMemo(
    () => answers.filter((v) => v !== null && v >= 3).length,
    [answers]
  );

  const setAnswer = (i, v) => {
    setAnswers((prev) => {
      const next = [...prev];
      next[i] = v;
      return next;
    });
  };
  const resetAnswers = () =>
    setAnswers(Array(NIJMEGEN_ITEMS.length).fill(null));

  const scoreInterpretation = () => {
    if (answeredCount === 0)
      return {
        color: C.muted,
        label: 'Répondez aux questions pour obtenir votre score.',
      };
    if (totalScore > 23)
      return {
        color: C.amber,
        label:
          'Score supérieur à 23 : suggère un syndrome d’hyperventilation. Un bilan est recommandé.',
      };
    if (highFreqCount >= 3)
      return {
        color: C.primaryDark,
        label:
          'Au moins 3 symptômes cotés « souvent » ou « très souvent » : oriente vers un trouble du schéma respiratoire.',
      };
    return {
      color: C.primary,
      label:
        'Score inférieur au seuil : profil peu évocateur d’hyperventilation, mais un avis reste utile en cas de gêne.',
    };
  };
  const scoreState = scoreInterpretation();

  const symptomGroups = [
    {
      icon: Wind,
      title: 'Respiration',
      items: [
        'Sensation de « faim d’air » ou de manque d’air',
        'Besoin fréquent de soupirer ou de bâiller',
        'Impossibilité d’inspirer « à fond »',
        'Essoufflement disproportionné par rapport à l’effort',
        'Respiration rapide, profonde ou irrégulière',
        'Gêne respiratoire diffuse',
      ],
    },
    {
      icon: Heart,
      title: 'Thorax et cœur',
      items: [
        'Oppression ou douleur thoracique',
        'Palpitations, sensation de cœur qui s’emballe',
        'Battements irréguliers',
      ],
    },
    {
      icon: Brain,
      title: 'Tête et nerfs',
      items: [
        'Vertiges ou sensation de « tête qui tourne »',
        'Vision floue',
        'Sensation d’irréalité, d’être « ailleurs »',
        'Confusion, difficulté à se concentrer',
        'Fourmillements autour de la bouche ou dans les doigts',
        'Mains ou pieds froids, mains moites',
      ],
    },
    {
      icon: Activity,
      title: 'Muscles, posture, digestion',
      items: [
        'Raideurs du cou, des épaules et du dos',
        'Doigts ou bras raides, crampes',
        'Ventre gonflé, gêne abdominale, nausées',
        'Tension de la mâchoire',
      ],
    },
    {
      icon: Smile,
      title: 'Émotions et sommeil',
      items: [
        'Anxiété, irritabilité, hypervigilance',
        'Peur de manquer d’air',
        'Sommeil perturbé, fatigue constante',
        'Confiance en soi ébranlée',
      ],
    },
  ];

  const forms = [
    {
      title: 'L’hyperventilation chronique',
      desc: 'On déplace trop d’air, trop souvent, sans raison métabolique. Le CO₂ chute et la fameuse cascade de symptômes (vertiges, fourmillements, palpitations, faim d’air) s’installe.',
    },
    {
      title: 'La respiration thoracique haute',
      desc: 'Les épaules et le haut de la poitrine se soulèvent à chaque inspiration, le diaphragme est sous-utilisé. Fréquente en cas de stress chronique ou de posture affaissée ; coûteuse en énergie et source de tensions du cou.',
    },
    {
      title: 'Les soupirs et bâillements excessifs',
      desc: 'Soupirs répétés, bâillements à répétition, envie permanente de « reprendre son souffle » : ce schéma maintient une ventilation excessive en boucle.',
    },
    {
      title: 'La respiration buccale',
      desc: 'Respirer par la bouche au repos contourne les fonctions du nez (filtration, réchauffement, humidification) et favorise une respiration plus ample, plus rapide et moins stable.',
    },
    {
      title: 'L’apnée d’écran et les blocages',
      desc: 'Blocages respiratoires répétés en situation de concentration ou de stress (devant un écran, en conduisant…). La respiration devient saccadée, on « oublie de respirer » puis on compense par de grandes inspirations.',
    },
    {
      title: 'La respiration paradoxale ou désynchronisée',
      desc: 'Thorax et abdomen ne bougent plus en harmonie : l’abdomen se creuse à l’inspiration au lieu de se gonfler, ou les mouvements sont anarchiques. C’est souvent la forme la plus visible à l’examen.',
    },
  ];

  const causes = [
    {
      title: 'Le stress',
      desc: 'Le grand perturbateur n°1. Stress aigu (échéance, examen, conflit) ou chronique (surmenage, charge mentale) : il accélère et amplifie la respiration, souvent sans que l’on s’en aperçoive.',
    },
    {
      title: 'Les émotions',
      desc: 'Anxiété, peur, deuil, contrariété… Les émotions fortes modifient le rythme respiratoire. À force de répétition, la modification devient une habitude.',
    },
    {
      title: 'La posture',
      desc: 'Affaissement devant les écrans, « nuque en avant » (i-hunch), position assise prolongée : la cage thoracique est comprimée, le diaphragme est limité, on bascule vers une respiration thoracique haute.',
    },
    {
      title: 'L’« apnée d’écran »',
      desc: 'En lisant des e-mails, des messages ou en se concentrant sur un écran, beaucoup de personnes bloquent involontairement leur respiration (screen apnoea). Ce blocage répété dérègle le schéma respiratoire.',
    },
    {
      title: 'Les habitudes respiratoires',
      desc: 'Respiration buccale, soupirs répétés, tentatives de « grandes inspirations » qui, paradoxalement, entretiennent l’hyperventilation et la sensation de manquer d’air.',
    },
    {
      title: 'Maladies et situations associées',
      desc: 'Asthme, BPCO, covid long, douleurs chroniques, troubles anxieux, grossesse, voix, chant ou sport intensif : autant de situations qui peuvent désorganiser la respiration et la maintenir en dysfonction.',
    },
  ];

  const exercises = [
    {
      title: 'La prise de conscience',
      duration: '1 à 2 min',
      steps: [
        'Asseyez-vous confortablement, dos droit, épaules relâchées.',
        'Fermez les yeux ou regardez au loin.',
        'Pendant une à deux minutes, observez votre respiration sans chercher à la modifier : respirez-vous par le nez ou par la bouche ? Le haut de la poitrine bouge-t-il, ou le bas des côtes ? Y a-t-il des soupirs, des blocages, des pauses ?',
        'Notez aussi les tensions : cou, épaules, mâchoire, ventre.',
      ],
      closing: 'Cet exercice simple est la base de tout : on ne peut changer que ce que l’on perçoit.',
    },
    {
      title: 'La respiration nasale calme',
      duration: '3 à 5 min',
      steps: [
        'Bouche fermée, respirez doucement par le nez.',
        'Laissez l’inspiration être légère et silencieuse — pas besoin de « remplir » les poumons.',
        'Laissez l’expiration s’écouler tranquillement, légèrement plus longue que l’inspiration, sans la forcer.',
        'Visez un rythme proche de 10 à 14 cycles par minute, avec une petite pause détendue en fin d’expiration.',
      ],
      closing: 'Astuce : placez une main sur le haut de la poitrine. Elle doit rester presque immobile.',
    },
    {
      title: 'La respiration costo-diaphragmatique (allongé)',
      duration: '3 à 5 min',
      steps: [
        'Allongez-vous sur le dos, genoux fléchis, pieds à plat, tête soutenue si besoin.',
        'Placez une main sur le bas de la cage thoracique (sur le côté), l’autre sur le haut de la poitrine.',
        'À l’inspiration, sentez les côtes inférieures et le ventre se dilater doucement, comme si vous gonfliez un ballon dans le bas des côtes.',
        'À l’expiration, laissez tout se détendre et se vider sans effort.',
        'La main du haut de la poitrine doit rester calme.',
      ],
      closing: 'Il ne s’agit pas de pousser fortement le ventre ni de prendre de grandes inspirations : juste un mouvement souple et régulier.',
    },
    {
      title: 'L’expiration allongée et la pause détendue',
      duration: '2 à 3 min',
      steps: [
        'Assis ou allongé, respirez par le nez.',
        'Laissez l’expiration être un peu plus longue que l’inspiration (par exemple 4 s / 6 s).',
        'En fin d’expiration, laissez une pause naturelle et confortable, sans blocage ni effort.',
        'Ne cherchez jamais à « vider » complètement vos poumons ni à retenir votre souffle.',
      ],
      closing: 'C’est l’exercice de prédilection avant une situation stressante : un entretien, un examen, une réunion.',
    },
    {
      title: 'L’intégration dans le mouvement',
      duration: 'selon activité',
      steps: [
        'Marche : respirez par le nez, en cherchant un rythme régulier (par exemple inspirer sur 2 pas, expirer sur 3 à 4 pas — à adapter à votre niveau).',
        'Escaliers : expirez pendant la phase d’effort (la montée), inspirez pendant la phase de récupération.',
        'Sport : évitez de bloquer votre respiration pendant l’effort ; expirez pendant le geste exigeant (comme en musculation ou en course).',
        'Parole : parlez en utilisant des expirations régulières, en vous arrêtant pour reprendre une inspiration nasale plutôt que de parler « à bout de souffle ».',
        'Concentration : toutes les 20-30 minutes devant un écran, faites 3 cycles de respiration nasale calme pour rompre l’« apnée d’écran ».',
      ],
      closing: 'C’est l’étape la plus importante : elle fait passer la respiration saine du statut d’« exercice » à celui d’« habitude de vie ».',
    },
  ];

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: C.white, fontFamily: 'Figtree, sans-serif' }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500&family=Figtree:wght@400;500;600;700&display=swap');
        @keyframes heroBreathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
        @keyframes heroBreatheShadow {
          0%, 100% {
            box-shadow: 0 0 40px rgba(14, 165, 233, 0.30),
                        0 0 80px rgba(14, 165, 233, 0.10);
          }
          50% {
            box-shadow: 0 0 60px rgba(14, 165, 233, 0.50),
                        0 0 120px rgba(14, 165, 233, 0.20);
          }
        }
        .hero-breathing-orb {
          animation: heroBreathe 4s ease-in-out infinite,
                     heroBreatheShadow 4s ease-in-out infinite;
          will-change: transform, box-shadow;
        }
      `}</style>

      <SiteHeaderV2 />

      {/* ============================ HERO ============================ */}
      <section className="py-16 lg:py-24" style={{ backgroundColor: C.cloud }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <PageTitle
                kicker="Information patient · Méthode BradCliff®"
                title="La respiration"
                highlight="dysfonctionnelle"
                subtitle="Comprendre la respiration dysfonctionnelle : symptômes, causes, diagnostic et rééducation avec la méthode BradCliff® pratiquée dans notre cabinet de kinésithérapie."
                align="left"
                className="mb-6"
              />
              <p
                className="text-xl sm:text-2xl leading-snug mb-8"
                style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: C.primaryDark }}
              >
                Comprendre et retrouver une respiration saine.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/evaluer-respiration"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-base font-medium transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                  style={{ backgroundColor: C.primary, color: C.white }}
                  data-testid="hero-cta-evaluate"
                >
                  Évaluer votre respiration
                  <ChevronRight size={18} />
                </Link>
                <a
                  href="#nijmegen"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-base font-medium transition-all duration-300"
                  style={{
                    backgroundColor: C.white,
                    color: C.primaryDark,
                    border: `1px solid ${C.border}`,
                  }}
                  data-testid="hero-cta-nijmegen"
                >
                  Questionnaire de Nijmegen
                </a>
              </div>
            </div>

            <div className="flex justify-center">
              <div className="flex items-center justify-center">
                <div
                  className="hero-breathing-orb w-72 h-72 sm:w-80 sm:h-80 lg:w-96 lg:h-96 rounded-full overflow-hidden"
                  style={{ border: `4px solid ${C.skySoft}` }}
                >
                  <img
                    src="/lungs-dysfunctional.jpg"
                    alt="Poumons - Respiration dysfonctionnelle"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: 'center 60%' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================== 1. Qu'est-ce que la respiration dysf. ================== */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <SectionLabel number="1">Comprendre</SectionLabel>
          <SectionTitle>Qu’est-ce que la respiration dysfonctionnelle ?</SectionTitle>
          <div className="space-y-5 text-lg leading-relaxed" style={{ color: C.slate }}>
            <p>
              Respirer est le geste le plus naturel du monde… jusqu’au jour où il devient
              une source d’inconfort, d’essoufflement ou d’angoisse.
            </p>
            <p>
              La <strong>respiration dysfonctionnelle</strong> (en anglais{' '}
              <em>breathing pattern disorder</em>, « trouble du schéma respiratoire »)
              désigne une respiration devenue{' '}
              <strong>inadaptée aux besoins réels de l’organisme</strong> : trop rapide,
              trop ample, irrégulière, effectuée par la bouche, ou confinée au haut de la poitrine.
            </p>
            <p>
              Point essentiel à comprendre : cela{' '}
              <strong>ne signifie pas que vos poumons sont malades</strong>. Dans la très
              grande majorité des cas, ils sont parfaitement sains. C’est le{' '}
              <em>schéma</em> — la « programmation » de votre respiration — qui s’est
              déréglé, souvent progressivement, à force d’habitudes, de stress ou de postures.
            </p>
            <p>
              Ce trouble est fréquent, mais largement méconnu. Il peut exister seul ou
              s’associer à d’autres affections (asthme, BPCO, covid long, troubles
              anxieux, douleurs chroniques…). La bonne nouvelle : il se corrige très bien
              avec une rééducation adaptée.
            </p>
          </div>

          {/* Healthy breathing table */}
          <div className="mt-12">
            <h3
              className="text-2xl mb-4"
              style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
            >
              Qu’est-ce qu’une respiration saine au repos ?
            </h3>
            <p className="text-base leading-relaxed mb-6" style={{ color: C.slate }}>
              Pour repérer ce qui ne va pas, il faut savoir à quoi ressemble une respiration
              fonctionnelle. Au repos, une respiration saine se reconnaît à ces caractéristiques :
            </p>
            <div
              className="rounded-2xl overflow-hidden"
              style={{ border: `1px solid ${C.border}`, backgroundColor: C.white }}
            >
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ backgroundColor: C.skyPale }}>
                    <th
                      className="py-3 px-5 text-left font-medium"
                      style={{ color: C.primaryDark, width: '38%' }}
                    >
                      Caractéristique
                    </th>
                    <th
                      className="py-3 px-5 text-left font-medium"
                      style={{ color: C.primaryDark }}
                    >
                      Respiration saine
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Voie d’entrée', 'Par le nez (filtre, réchauffe, humidifie l’air)'],
                    ['Fréquence', 'Environ 10 à 14 cycles par minute'],
                    ['Zone de mouvement', 'Bas des côtes et abdomen (le diaphragme travaille)'],
                    ['Bruit', 'Silencieuse et discrète'],
                    ['Ampleur', 'Modérée — on ne « remplit » pas ses poumons au repos'],
                    ['Après l’expiration', 'Une petite pause détendue avant l’inspiration suivante'],
                    ['Adaptabilité', 'S’ajuste à l’effort, à la parole, à l’émotion, sans blocage'],
                  ].map(([k, v], i) => (
                    <tr
                      key={k}
                      style={{
                        backgroundColor: i % 2 === 0 ? C.cloud : C.white,
                        borderTop: `1px solid ${C.border}`,
                      }}
                    >
                      <td
                        className="py-3 px-5 font-medium"
                        style={{ color: C.ink, width: '38%' }}
                      >
                        {k}
                      </td>
                      <td className="py-3 px-5" style={{ color: C.slate }}>
                        {v}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-base leading-relaxed mt-6" style={{ color: C.slate }}>
              Une respiration dysfonctionnelle s’écarte d’un ou plusieurs de ces critères :
              on respire par la bouche, le haut de la poitrine se soulève, les épaules montent,
              on soupire sans arrêt, on bloque sa respiration, ou on respire « trop » sans s’en
              rendre compte.
            </p>
          </div>
        </div>
      </section>

      {/* ================== 2. Signes et symptômes ================== */}
      <section className="py-16" style={{ backgroundColor: C.cloud }}>
        <div className="max-w-6xl mx-auto px-6">
          <SectionLabel number="2">Reconnaître</SectionLabel>
          <SectionTitle>Les signes et symptômes</SectionTitle>
          <p className="text-lg mb-10 leading-relaxed max-w-3xl" style={{ color: C.slate }}>
            Les symptômes de la respiration dysfonctionnelle sont nombreux et variés. Ils
            peuvent apparaître au repos, à l’effort, ou dans les situations stressantes.
            S’ils sont impressionnants, ils ne signifient pas que vous manquez d’oxygène :
            ils résultent surtout du déséquilibre provoqué par une ventilation excessive et
            de la tension musculaire associée.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {symptomGroups.map((g) => (
              <SymptomGroup key={g.title} {...g} />
            ))}
          </div>

          <div className="mt-10 space-y-4">
            <Callout variant="info" icon={ClipboardList} title="À retenir">
              Ces symptômes <strong>peuvent imiter une maladie grave</strong> (cardiaque,
              pulmonaire, neurologique). C’est pourquoi une évaluation médicale puis un
              bilan en kinésithérapie sont indispensables avant de conclure à une respiration
              dysfonctionnelle.
            </Callout>
            <Callout variant="danger" icon={AlertTriangle} title="⚠ Symptômes d’alerte — consultez sans attendre">
              Douleur thoracique brutale ou intense, essoufflement soudain et sévère,
              malaise ou évanouissement, lèvres ou visage bleutés, difficulté à parler.
              Si vous présentez un de ces signes, appelez les{' '}
              <strong>urgences (144)</strong>. Voir la section « Quand consulter » en fin
              de page.
            </Callout>
          </div>
        </div>
      </section>

      {/* ================== 3. Causes ================== */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <SectionLabel number="3">Origines</SectionLabel>
          <SectionTitle>Les causes et facteurs déclenchants</SectionTitle>
          <p className="text-lg mb-10 leading-relaxed max-w-3xl" style={{ color: C.slate }}>
            Une respiration dysfonctionnelle ne tombe pas du ciel : elle s’installe
            souvent à la faveur d’un ou plusieurs facteurs, qui entretiennent ensuite
            le trouble.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {causes.map((c, i) => (
              <div
                key={c.title}
                className="rounded-2xl p-6 h-full transition-all duration-300 hover:-translate-y-1"
                style={{
                  backgroundColor: C.white,
                  border: `1px solid ${C.border}`,
                }}
              >
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center mb-3 text-sm font-semibold"
                  style={{ backgroundColor: C.skyPale, color: C.primaryDark }}
                >
                  {i + 1}
                </div>
                <h3
                  className="text-lg mb-2"
                  style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
                >
                  {c.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: C.slate }}>
                  {c.desc}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-8">
            <Callout icon={Search} title="Bon à savoir">
              Le plus souvent, ce n’est pas <em>une</em> cause mais un{' '}
              <em>enchevêtrement</em> de facteurs qui installe le trouble. Identifier les
              vôtres fait partie intégrante de la prise en charge : c’est l’une des
              étapes clés du bilan et de la méthode BradCliff®.
            </Callout>
          </div>
        </div>
      </section>

      {/* ================== 4. Cercle vicieux ================== */}
      <section className="py-16" style={{ backgroundColor: C.cloud }}>
        <div className="max-w-4xl mx-auto px-6">
          <SectionLabel number="4">Mécanisme</SectionLabel>
          <SectionTitle>Le cercle vicieux de la respiration dysfonctionnelle</SectionTitle>
          <p className="text-lg mb-10 leading-relaxed" style={{ color: C.slate }}>
            Comprendre le mécanisme, c’est déjà commencer à en sortir. Voici comment le
            trouble s’auto-entretient :
          </p>
          <ol className="space-y-5">
            {[
              [
                'Un déclencheur apparaît',
                'Stress, posture, habitude, effort mal géré — et provoque une respiration excessive ou perturbée (trop d’air, respiration buccale, thorax haut…).',
              ],
              [
                'Le dioxyde de carbone (CO₂) sanguin baisse',
                'En respirant au-delà des besoins, on « chasse » trop de CO₂ : c’est l’hyperventilation, qui provoque une alcalose respiratoire.',
              ],
              [
                'Les symptômes apparaissent',
                'Vertiges, fourmillements, oppression, palpitations, faim d’air… Paradoxalement, ce sont les effets de la baisse de CO₂, pas d’un manque d’oxygène.',
              ],
              [
                'Ces sensations font peur',
                'On devient anxieux, tendu, hypervigilant : on « écoute » sa respiration en permanence, on cherche à respirer plus fort…',
              ],
              [
                'On respire encore plus mal',
                'Ce qui aggrave les symptômes et l’anxiété. Le cercle est bouclé.',
              ],
            ].map(([title, desc], i) => (
              <li
                key={i}
                className="flex gap-5 rounded-2xl p-5"
                style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
              >
                <div
                  className="flex-shrink-0 flex items-center justify-center"
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    backgroundColor: C.primary,
                    color: C.white,
                    fontFamily: 'Outfit, sans-serif',
                    fontWeight: 500,
                    fontSize: '1.1rem',
                  }}
                >
                  {i + 1}
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold mb-1" style={{ color: C.ink }}>
                    {title}
                  </h4>
                  <p className="text-sm leading-relaxed" style={{ color: C.slate }}>
                    {desc}
                  </p>
                </div>
              </li>
            ))}
          </ol>

          <div
            className="mt-10 rounded-2xl p-8 text-center"
            style={{
              background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
              color: C.white,
            }}
          >
            <p
              className="text-2xl sm:text-3xl mb-2"
              style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}
            >
              « En cas de doute : expirez. »
            </p>
            <p className="text-sm opacity-90">
              Principe clé de la méthode BradCliff® — l’expiration complète et détendue
              est la première marche vers un schéma respiratoire sain.
            </p>
          </div>

          <p className="text-lg leading-relaxed mt-10" style={{ color: C.slate }}>
            La bonne nouvelle, c’est que ce cercle <strong>peut être brisé</strong>. La
            rééducation respiratoire (et en particulier la méthode BradCliff®) agit
            précisément à chacun de ces niveaux : elle calme la ventilation, relâche la
            tension, désamorce la peur de manquer d’air et réapprend au corps un schéma
            sain, étape par étape.
          </p>
        </div>
      </section>

      {/* ================== 5. Formes ================== */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <SectionLabel number="5">Typologie</SectionLabel>
          <SectionTitle>Les différentes formes de respiration dysfonctionnelle</SectionTitle>
          <p className="text-lg mb-10 leading-relaxed max-w-3xl" style={{ color: C.slate }}>
            Il n’existe pas « une » respiration dysfonctionnelle, mais plusieurs schémas
            perturbés, souvent combinés. Votre praticien identifiera le vôtre lors du bilan.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {forms.map((f) => (
              <div
                key={f.title}
                className="rounded-2xl p-6 h-full transition-all duration-300 hover:shadow-md"
                style={{
                  backgroundColor: C.white,
                  border: `1px solid ${C.border}`,
                }}
              >
                <h3
                  className="text-lg mb-3"
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    fontWeight: 400,
                    color: C.ink,
                  }}
                >
                  {f.title}
                </h3>
                <p className="text-sm leading-relaxed" style={{ color: C.slate }}>
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================== 6. Bilan ================== */}
      <section className="py-16" style={{ backgroundColor: C.cloud }}>
        <div className="max-w-6xl mx-auto px-6">
          <SectionLabel number="6">Diagnostic</SectionLabel>
          <SectionTitle>Le bilan au cabinet : comment pose-t-on le diagnostic ?</SectionTitle>
          <p className="text-lg mb-10 leading-relaxed max-w-3xl" style={{ color: C.slate }}>
            Le diagnostic repose d’abord sur une évaluation médicale (pour écarter une
            cause organique), puis sur un bilan kinésithérapique complet. Voici comment se
            déroule ce bilan.
          </p>
          <h3
            className="text-2xl mb-6"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
          >
            Les 4 étapes du bilan
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <StepCard number="1" title="L’entretien">
              Histoire des symptômes : quand ont-ils commencé, à quelles occasions, depuis
              combien de temps ? Antécédents, traitements, activités, sommeil, niveau de stress.
            </StepCard>
            <StepCard number="2" title="Le questionnaire de Nijmegen">
              Un outil de dépistage de 16 symptômes, rapide à remplir (voir ci-dessous). Il
              oriente vers une hyperventilation ou un trouble du schéma respiratoire.
            </StepCard>
            <StepCard number="3" title="L’observation de la respiration">
              Au repos puis au mouvement : voie nasale ou buccale, fréquence, rythme, soupirs,
              blocages, zone de mouvement (haut du thorax ou bas des côtes), coordination
              thorax-abdomen.
            </StepCard>
            <StepCard number="4" title="L’examen postural et musculaire">
              Posture globale, mobilité de la cage thoracique, tensions du cou, des épaules
              et de la mâchoire, force des muscles respiratoires si nécessaire.
            </StepCard>
          </div>
        </div>
      </section>

      {/* ================== 7. Nijmegen (interactif) ================== */}
      <section id="nijmegen" className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <SectionLabel>Évaluation de l’hyperventilation</SectionLabel>
          <SectionTitle>Le questionnaire de Nijmegen</SectionTitle>
          <p className="text-lg mb-4 leading-relaxed" style={{ color: C.slate }}>
            Le <strong>questionnaire de Nijmegen</strong> est un outil international de
            dépistage spécifiquement dédié à l’<strong>évaluation des symptômes du syndrome
            d’hyperventilation</strong> (chronique ou épisodique). Il ne mesure pas
            directement la mécanique respiratoire, mais la <em>charge symptomatique</em>
            typique d’une respiration excessive par rapport aux besoins métaboliques.
          </p>
          <p className="text-lg mb-8 leading-relaxed" style={{ color: C.slate }}>
            Il comporte 16 symptômes, chacun coté de 0 (jamais) à 4 (très souvent). Vous
            pouvez le remplir ci-dessous avant votre rendez-vous : cela fait gagner un
            temps précieux au cabinet.
          </p>

          {/* Score panel */}
          <div
            className="rounded-2xl p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
            style={{
              backgroundColor: C.skyPale,
              border: `1px solid ${C.skySoft}`,
            }}
          >
            <div>
              <p
                className="text-xs uppercase tracking-[0.2em] mb-1"
                style={{ color: C.primaryDark, fontWeight: 500 }}
              >
                Votre score total
              </p>
              <p
                className="text-4xl sm:text-5xl"
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  fontWeight: 300,
                  color: C.ink,
                }}
                data-testid="nijmegen-total-score"
              >
                {totalScore}{' '}
                <span className="text-2xl" style={{ color: C.muted }}>
                  / 64
                </span>
              </p>
              <p className="text-xs mt-1" style={{ color: C.slate }}>
                {answeredCount} / {NIJMEGEN_ITEMS.length} questions répondues
              </p>
            </div>
            <button
              type="button"
              onClick={resetAnswers}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors"
              style={{
                backgroundColor: C.white,
                color: C.primaryDark,
                border: `1px solid ${C.border}`,
              }}
              data-testid="nijmegen-reset"
            >
              <RotateCcw size={14} />
              Effacer mes réponses
            </button>
          </div>

          <div
            className="rounded-xl p-4 mb-8 text-sm"
            style={{
              backgroundColor: C.white,
              border: `1px solid ${C.border}`,
              color: scoreState.color,
              fontWeight: 500,
            }}
            data-testid="nijmegen-interpretation"
          >
            {scoreState.label}
          </div>

          <div className="space-y-3">
            {NIJMEGEN_ITEMS.map((label, i) => (
              <div
                key={label}
                className="rounded-xl p-4 sm:p-5"
                style={{
                  backgroundColor: C.white,
                  border: `1px solid ${answers[i] !== null ? C.skySoft : C.border}`,
                }}
              >
                <p
                  className="text-sm font-medium mb-3"
                  style={{ color: C.ink, fontFamily: 'Figtree, sans-serif' }}
                >
                  {i + 1}. {label}
                </p>
                <div className="flex flex-wrap gap-2">
                  {NIJMEGEN_LEVELS.map((lvl) => {
                    const active = answers[i] === lvl.value;
                    return (
                      <button
                        key={lvl.value}
                        type="button"
                        onClick={() => setAnswer(i, lvl.value)}
                        className="text-xs px-3 py-2 rounded-full transition-all"
                        style={{
                          backgroundColor: active ? C.primary : C.cloud,
                          color: active ? C.white : C.slate,
                          border: `1px solid ${active ? C.primary : C.border}`,
                          fontWeight: active ? 600 : 400,
                        }}
                        data-testid={`nijmegen-q${i}-l${lvl.value}`}
                      >
                        {lvl.value} · {lvl.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <Callout icon={ClipboardList} title="Lecture des résultats">
              Un score supérieur à <strong>23 / 64</strong> suggère un syndrome
              d’hyperventilation. Un score inférieur à 23 mais avec{' '}
              <strong>au moins 3 symptômes cotés « souvent » ou « très souvent »</strong> peut
              orienter vers un trouble du schéma respiratoire plutôt qu’une
              hyperventilation chronique. <strong>Attention :</strong> ce questionnaire est
              un outil d’orientation, <em>pas un diagnostic</em>. Un score élevé peut
              aussi s’expliquer par de l’anxiété, un asthme, une maladie
              cardiovasculaire, une anémie ou d’autres problèmes. Son interprétation se
              fait toujours avec votre praticien.
            </Callout>
          </div>
        </div>
      </section>

      {/* ================== 7b. BPAT ================== */}
      <section id="bpat" className="py-16" style={{ backgroundColor: C.cloud }}>
        <div className="max-w-4xl mx-auto px-6">
          <SectionLabel>Évaluation du schéma respiratoire</SectionLabel>
          <SectionTitle>L’évaluation BPAT</SectionTitle>
          <div className="space-y-5 text-lg leading-relaxed" style={{ color: C.slate }}>
            <p>
              Le <strong>BPAT</strong> (<em>Breathing Pattern Assessment Tool</em>, ou
              <em> Brompton Breathing Pattern Assessment Tool</em>) est un outil
              d’<strong>observation clinique</strong> mis au point à l’Imperial College
              London / Royal Brompton Hospital. Il complète le questionnaire de Nijmegen
              en s’intéressant, non plus aux symptômes ressentis, mais à la{' '}
              <strong>mécanique respiratoire elle-même</strong> : comment vous respirez au
              repos.
            </p>
            <p>
              Il est particulièrement adapté à la <strong>rééducation de la respiration
              dysfonctionnelle</strong> : il permet d’objectiver le schéma respiratoire de
              départ, de repérer précisément les éléments à corriger, puis de mesurer les
              progrès au fil des séances.
            </p>
          </div>

          <div className="mt-10">
            <h3
              className="text-2xl mb-4"
              style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
            >
              Comment se déroule l’évaluation ?
            </h3>
            <ul className="space-y-3 text-base leading-relaxed mb-8" style={{ color: C.slate }}>
              {[
                'Vous êtes installé(e) confortablement, assis, dos soutenu, au calme.',
                'Après au moins 5 minutes de repos, votre praticien vous observe pendant 1 minute exactement, sans consigne particulière.',
                'Sept domaines sont cotés sur une échelle de 0 à 2 (0 = normal, 2 = franchement altéré).',
                'Le score total va de 0 à 14. Un score ≥ 4 est en faveur d’un trouble du schéma respiratoire (breathing pattern disorder).',
              ].map((li) => (
                <li key={li} className="flex gap-3">
                  <span
                    className="mt-2 rounded-full flex-shrink-0"
                    style={{ width: 6, height: 6, backgroundColor: C.primary }}
                  />
                  <span>{li}</span>
                </li>
              ))}
            </ul>

            <h3
              className="text-2xl mb-4"
              style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
            >
              Les 7 domaines observés
            </h3>
            <div
              className="rounded-2xl overflow-hidden"
              style={{ border: `1px solid ${C.border}`, backgroundColor: C.white }}
            >
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ backgroundColor: C.skyPale }}>
                    <th
                      className="py-3 px-5 text-left font-medium"
                      style={{ color: C.primaryDark, width: '38%' }}
                    >
                      Domaine
                    </th>
                    <th
                      className="py-3 px-5 text-left font-medium"
                      style={{ color: C.primaryDark }}
                    >
                      Ce qui est observé
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    [
                      '1. Mouvement thoraco-abdominal',
                      'Zone qui se mobilise à l’inspiration : thoracique haute, abdominale (diaphragmatique), ou mixte / paradoxale.',
                    ],
                    [
                      '2. Flux inspiratoire',
                      'Caractère audible ou non de l’inspiration (souffle, sifflement, bruit).',
                    ],
                    [
                      '3. Flux expiratoire',
                      'Caractère audible ou non de l’expiration (souffle, sifflement, bruit).',
                    ],
                    [
                      '4. Voie inspiratoire et expiratoire',
                      'Respiration par le nez, par la bouche, ou combinaison des deux.',
                    ],
                    [
                      '5. Signes de « faim d’air »',
                      'Fréquence des soupirs, bâillements ou grandes inspirations réflexes durant l’observation.',
                    ],
                    [
                      '6. Fréquence respiratoire',
                      'Nombre de cycles par minute (repère : ≤ 12 ; 13-25 ; > 25).',
                    ],
                    [
                      '7. Rythme',
                      'Respiration régulière et rythmée, ou irrégulière / erratique.',
                    ],
                  ].map(([k, v], i) => (
                    <tr
                      key={k}
                      style={{
                        backgroundColor: i % 2 === 0 ? C.cloud : C.white,
                        borderTop: `1px solid ${C.border}`,
                      }}
                    >
                      <td
                        className="py-3 px-5 font-medium"
                        style={{ color: C.ink, width: '38%' }}
                      >
                        {k}
                      </td>
                      <td className="py-3 px-5" style={{ color: C.slate }}>
                        {v}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div
              className="rounded-2xl p-6 mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
              style={{
                backgroundColor: C.skyPale,
                border: `1px solid ${C.skySoft}`,
              }}
            >
              <div>
                <p
                  className="text-xs uppercase tracking-[0.2em] mb-1"
                  style={{ color: C.primaryDark, fontWeight: 500 }}
                >
                  Interprétation
                </p>
                <p
                  className="text-3xl sm:text-4xl"
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    fontWeight: 300,
                    color: C.ink,
                  }}
                >
                  Score ≥ 4 <span className="text-lg" style={{ color: C.muted }}>/ 14</span>
                </p>
                <p className="text-sm mt-1" style={{ color: C.slate }}>
                  Suggère un trouble du schéma respiratoire (BrPD).
                </p>
              </div>
            </div>

            <div className="mt-8 space-y-4">
              <Callout icon={Eye} title="Nijmegen et BPAT : deux outils, deux angles">
                Le <strong>Nijmegen</strong> évalue les <em>symptômes</em> de
                l’hyperventilation ressentis par le patient. Le <strong>BPAT</strong>
                objective la <em>mécanique respiratoire</em> observée par le praticien.
                Utilisés ensemble, ils permettent une lecture beaucoup plus fine du
                trouble et guident précisément la rééducation.
              </Callout>
              <Callout icon={ClipboardList} title="Bon à savoir">
                Le BPAT est un outil clinique dont l’usage est réservé aux professionnels
                de santé formés. Sa cotation est effectuée par votre praticien lors du
                bilan, sur la base d’une observation standardisée. L’outil est développé
                et sous copyright de l’Imperial College London.
              </Callout>
            </div>
          </div>
        </div>
      </section>

      {/* ================== 7. Méthode BradCliff ================== */}
      <section className="py-16" style={{ backgroundColor: C.cloud }}>
        <div className="max-w-6xl mx-auto px-6">
          <SectionLabel number="7">Notre approche</SectionLabel>
          <SectionTitle>La méthode BradCliff®</SectionTitle>
          <p className="text-lg mb-10 leading-relaxed max-w-3xl" style={{ color: C.slate }}>
            C’est la méthode de référence que nous utilisons dans notre cabinet pour
            évaluer et rééduquer la respiration dysfonctionnelle.
          </p>

          <h3
            className="text-2xl mb-4"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
          >
            D’où vient la méthode BradCliff® ?
          </h3>
          <div className="space-y-5 text-lg leading-relaxed mb-12" style={{ color: C.slate }}>
            <p>
              La <strong>méthode BradCliff®</strong> a été développée par{' '}
              <strong>Dinah Bradley</strong> et <strong>Tania Clifton-Smith</strong>, deux
              physiothérapeutes néo-zélandaises expertes en rééducation respiratoire,
              fondatrices du centre <em>Breathing Works</em> à Auckland. Elle est aujourd’hui
              <strong> reconnue internationalement</strong> et enseignée à des professionnels
              de santé du monde entier (kinésithérapeutes, physiothérapeutes, médecins).
            </p>
            <p>
              Sa devise : <em>« Better Breathing for Better Health »</em> — mieux respirer
              pour mieux vivre — et surtout <strong>« des compétences, pas des pilules »</strong>
              {' '}(<em>skills, not pills</em>) : la méthode privilégie l’apprentissage
              d’outils concrets et durables plutôt que des solutions toutes faites.
            </p>
          </div>

          <h3
            className="text-2xl mb-4"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
          >
            L’objectif : pas « respirer plus fort », mais respirer mieux
          </h3>
          <p className="text-lg leading-relaxed mb-6" style={{ color: C.slate }}>
            Contrairement à une idée reçue, l’objectif n’est pas de « prendre de
            grandes inspirations » ni de « remplir ses poumons ». Il s’agit de réapprendre
            une respiration :
          </p>
          <div
            className="rounded-2xl p-6 sm:p-8 mb-6"
            style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
          >
            <ul className="space-y-3">
              {[
                'calme et régulière, adaptée à l’activité ;',
                'essentiellement nasale lorsque c’est possible ;',
                'silencieuse et peu ample au repos ;',
                'à dominante costo-diaphragmatique (mouvement souple du bas des côtes et de l’abdomen) ;',
                'coordonnée avec la posture, le mouvement et l’effort ;',
                'compatible avec une bonne tolérance au CO₂ (on n’« hyperventile » plus) ;',
                'libérée de la peur de manquer d’air et de l’hypervigilance respiratoire.',
              ].map((item) => (
                <li key={item} className="flex gap-3 text-base" style={{ color: C.slate }}>
                  <span
                    className="mt-2 rounded-full flex-shrink-0"
                    style={{ width: 6, height: 6, backgroundColor: C.primary }}
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <p className="text-lg leading-relaxed mb-12" style={{ color: C.slate }}>
            La prise en charge est toujours <strong>individualisée</strong> : chaque patient
            a son propre schéma perturbé, ses propres déclencheurs et ses propres objectifs.
          </p>

          <h3
            className="text-2xl mb-4"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
          >
            Le programme en 4 temps
          </h3>
          <p className="text-lg leading-relaxed mb-8" style={{ color: C.slate }}>
            La rééducation selon la méthode BradCliff® suit une progression structurée,
            enseignée et supervisée par votre praticien :
          </p>
          <div className="grid sm:grid-cols-2 gap-5">
            <StepCard number="1" title="Évaluation et définition des objectifs">
              Bilan complet (entretien, questionnaire de Nijmegen, observation de la
              respiration, posture), puis définition d’objectifs concrets et mesurables
              avec le patient.
            </StepCard>
            <StepCard number="2" title="Identification des déclencheurs et des facteurs de stress">
              Repérer ensemble ce qui déclenche ou aggrave la respiration dysfonctionnelle :
              stress, postures, écrans, habitudes, situations de la vie quotidienne.
            </StepCard>
            <StepCard number="3" title="Correction de la posture, des mécaniques corporelles et du schéma respiratoire">
              Travail sur la posture (notamment la position assise et l’affaissement
              « nuque en avant »), la mobilité de la cage thoracique, le relâchement du cou,
              des épaules et de la mâchoire, et la reprise d’un mouvement respiratoire
              costo-diaphragmatique.
            </StepCard>
            <StepCard number="4" title="Rééducation respiratoire et intégration dans la vie quotidienne">
              Prise de conscience du schéma, normalisation du rythme (respiration nasale
              calme, expiration douce), gestion de la relaxation et du stress, puis{' '}
              <strong>intégration fonctionnelle</strong> : marche, escaliers, sport, parole,
              situations stressantes. C’est l’étape qui transforme les exercices en
              habitude durable.
            </StepCard>
          </div>
          <div className="mt-8 space-y-4">
            <Callout icon={Activity} title="Un point encourageant">
              Selon les principes BradCliff®, il suffit d’environ{' '}
              <strong>24 heures de pratique cumulée</strong> pour modifier un schéma
              respiratoire — encore faut-il ensuite le consolider pour qu’il devienne
              la nouvelle habitude automatique. C’est ce travail de consolidation que
              nous accompagnons en séance et entre les séances.
            </Callout>
            <p className="text-base leading-relaxed" style={{ color: C.slate }}>
              Votre praticien a été formé à la méthode BradCliff® et l’utilise au
              quotidien dans notre cabinet. Chaque séance est adaptée à votre rythme, à vos
              progrès et à votre vie de tous les jours.
            </p>
          </div>
        </div>
      </section>

      {/* ================== 8. Exercices ================== */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <SectionLabel number="8">Pratique</SectionLabel>
          <SectionTitle>Les exercices de rééducation</SectionTitle>
          <p className="text-lg mb-8 leading-relaxed max-w-3xl" style={{ color: C.slate }}>
            Les exercices ci-dessous sont présentés à titre éducatif. Ils sont normalement
            <strong> enseignés et encadrés par votre praticien</strong>, qui les adaptera à
            votre cas. Commencez toujours dans un endroit calme, assis ou allongé, le nez dégagé.
          </p>

          <div className="mb-10">
            <Callout variant="warning" icon={AlertTriangle} title="⚠ Arrêtez immédiatement et signalez-le à votre praticien si vous ressentez">
              Des vertiges importants, une douleur thoracique, ou une aggravation nette de
              votre essoufflement. Ne pratiquez jamais d’apnées prolongées sans
              encadrement professionnel.
            </Callout>
          </div>

          <div
            className="rounded-2xl p-6 sm:p-8 mb-10 text-center"
            style={{
              background: `linear-gradient(135deg, ${C.skyPale} 0%, ${C.skySoft} 100%)`,
              border: `1px solid ${C.skySoft}`,
            }}
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <div>
                <p className="text-sm font-medium" style={{ color: C.primaryDark }}>
                  ↑ Inspirez par le nez (≈ 4 s)
                </p>
                <p className="text-sm font-medium mt-1" style={{ color: C.primaryDark }}>
                  ↓ Expirez doucement (≈ 6 s)
                </p>
              </div>
            </div>
            <p className="text-sm mt-4 max-w-xl mx-auto" style={{ color: C.slate }}>
              Suivez le mouvement du cercle : il grandit à l’inspiration, il rétrécit à
              l’expiration. Laissez l’expiration être plus longue que
              l’inspiration, sans forcer.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {exercises.map((e, i) => (
              <ExerciseCard key={e.title} index={i + 1} {...e} />
            ))}
          </div>

          {/* Practice tips */}
          <div className="mt-14">
            <h3
              className="text-2xl mb-6"
              style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 400, color: C.ink }}
            >
              Conseils pour une pratique efficace
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                {
                  title: 'Fréquence',
                  desc: 'De courtes sessions (5 à 10 minutes) 2 à 3 fois par jour valent mieux qu’une longue session hebdomadaire. La régularité fait tout.',
                },
                {
                  title: 'Qualité plutôt que quantité',
                  desc: 'Quelques minutes de respiration douce et détendue valent mieux que dix minutes d’efforts forcés. Dès que c’est inconfortable, relâchez l’intensité.',
                },
                {
                  title: 'Ancrez la pratique dans la journée',
                  desc: 'Choisissez des moments fixes : au réveil, avant un repas, avant de dormir, ou en attendant un rendez-vous. Associez-la à un geste existant (café, trajet…).',
                },
                {
                  title: 'Soyez patient·e',
                  desc: 'Les progrès ne sont pas linéaires : on avance, on régresse, on progresse. C’est normal. Le schéma ancien resurgit sous stress — c’est le moment de réutiliser vos outils.',
                },
              ].map((tip) => (
                <div
                  key={tip.title}
                  className="rounded-2xl p-5"
                  style={{ backgroundColor: C.cloud, border: `1px solid ${C.border}` }}
                >
                  <p
                    className="text-xs uppercase tracking-[0.2em] mb-2"
                    style={{ color: C.primaryDark, fontWeight: 500 }}
                  >
                    {tip.title}
                  </p>
                  <p className="text-sm leading-relaxed" style={{ color: C.slate }}>
                    {tip.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================== 9. Déroulement ================== */}
      <section className="py-16" style={{ backgroundColor: C.cloud }}>
        <div className="max-w-4xl mx-auto px-6">
          <SectionLabel number="9">Prise en charge</SectionLabel>
          <SectionTitle>Comment se déroule la rééducation ?</SectionTitle>
          <p className="text-lg mb-10 leading-relaxed" style={{ color: C.slate }}>
            Chaque parcours est unique, mais voici à quoi ressemble en général une prise en
            charge selon la méthode BradCliff® dans notre cabinet.
          </p>
          <div className="space-y-4">
            {[
              {
                icon: Stethoscope,
                title: '1. La séance de bilan (45 à 60 min)',
                desc: 'Entretien approfondi, questionnaire de Nijmegen, observation de la respiration, examen postural. Vous repartez avec une première compréhension de votre schéma respiratoire et les premiers outils.',
              },
              {
                icon: Activity,
                title: '2. Les séances de rééducation (environ 30 min)',
                desc: 'Apprentissage et correction des exercices, travail postural et de relâchement, intégration progressive dans vos activités. La fréquence (hebdomadaire, bimensuelle…) est adaptée à votre situation.',
              },
              {
                icon: Wind,
                title: '3. La pratique à la maison',
                desc: 'Répétition des exercices 5 à 10 minutes par jour, 2 à 3 fois par jour. C’est le moteur principal de la rééducation : la séance guide, la pratique quotidienne transforme.',
              },
              {
                icon: Eye,
                title: '4. La réévaluation',
                desc: 'Au fil des séances, le questionnaire de Nijmegen et l’observation permettent de mesurer les progrès et d’ajuster le programme. Beaucoup de patients constatent une amélioration nette en 4 à 6 semaines de pratique régulière, mais chaque parcours est différent.',
              },
              {
                icon: Smile,
                title: '5. L’autonomie',
                desc: 'L’objectif final : que vous soyez autonome, avec une boîte à outils respiratoires pour la vie — y compris pour gérer les moments de stress ou les rechutes occasionnelles.',
              },
            ].map((s) => (
              <div
                key={s.title}
                className="rounded-2xl p-5 flex gap-4"
                style={{ backgroundColor: C.white, border: `1px solid ${C.border}` }}
              >
                <div
                  className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: C.skySoft, color: C.primaryDark }}
                >
                  <s.icon size={20} />
                </div>
                <div>
                  <h4 className="font-semibold mb-1" style={{ color: C.ink }}>
                    {s.title}
                  </h4>
                  <p className="text-sm leading-relaxed" style={{ color: C.slate }}>
                    {s.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================== 10. FAQ ================== */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <SectionLabel number="10">FAQ</SectionLabel>
          <SectionTitle>Questions fréquentes</SectionTitle>
          <div className="space-y-3 mt-8">
            <FaqItem index={0} q="Est-ce que je manque vraiment d’oxygène ?">
              <p>
                <strong>Non, dans la très grande majorité des cas.</strong> Lors d’une
                respiration dysfonctionnelle, le sang est au contraire bien oxygéné —
                c’est même l’excès de ventilation qui pose problème, en faisant
                baisser le dioxyde de carbone (CO₂). Les sensations de faim d’air, de
                vertige ou de fourmillements sont liées à ce déséquilibre, pas à un manque
                d’oxygène. Votre praticien vérifiera ce point lors du bilan.
              </p>
            </FaqItem>
            <FaqItem index={1} q="Est-ce grave ?">
              <p>
                Ce n’est pas une maladie organique des poumons, mais ce n’est pas
                non plus « dans la tête » : c’est un vrai trouble fonctionnel, qui peut
                être très invalidant au quotidien (essoufflement, fatigue, anxiété, douleurs…).
                La bonne nouvelle, c’est qu’il se corrige remarquablement bien avec
                une rééducation adaptée.
              </p>
            </FaqItem>
            <FaqItem index={2} q="Combien de temps faut-il pour voir des résultats ?">
              <p>
                Beaucoup de patients ressentent une différence en quelques semaines, dès lors
                qu’ils pratiquent régulièrement (5 à 10 minutes par jour). En règle générale,
                comptez <strong>4 à 6 semaines</strong> de pratique régulière pour des
                changements nets et durables. Le parcours est ensuite consolidé sur plusieurs
                mois. Chacun évolue à son rythme.
              </p>
            </FaqItem>
            <FaqItem index={3} q="Dois-je faire les exercices tous les jours ?">
              <p>
                La régularité compte plus que la durée. De courtes sessions quotidiennes (ou
                presque) sont bien plus efficaces que de longues sessions occasionnelles.
                Votre praticien vous donnera un programme personnalisé, réaliste et compatible
                avec votre emploi du temps.
              </p>
            </FaqItem>
            <FaqItem index={4} q="La méthode BradCliff® remplace-t-elle mon traitement (asthme, etc.) ?">
              <p>
                <strong>Absolument pas.</strong> La rééducation respiratoire est{' '}
                <strong>complémentaire</strong> de votre traitement médical, jamais substitutive.
                Ne modifiez et n’arrêtez jamais un traitement sans avis médical. Pour
                l’asthme et d’autres maladies respiratoires, le travail de respiration
                se fait en lien étroit avec votre médecin, et peut aider à mieux contrôler les
                symptômes.
              </p>
            </FaqItem>
            <FaqItem index={5} q="Puis-je pratiquer seule à la maison ?">
              <p>
                Oui, après apprentissage avec votre praticien — c’est même essentiel à la
                réussite. En revanche, évitez de multiplier les exercices trouvés ici ou là
                sans encadrement : un schéma respiratoire se rééduque avec des consignes
                précises et adaptées. Et suivez toujours les règles de sécurité (arrêt en cas
                de vertiges importants, de douleur thoracique ou d’aggravation).
              </p>
            </FaqItem>
            <FaqItem index={6} q="Pourquoi mes symptômes reviennent-ils parfois, même quand ça va mieux ?">
              <p>
                C’est tout à fait normal : l’ancien schéma respiratoire resurgit
                volontiers dans les moments de stress, de fatigue ou de surcharge. Ce n’est
                pas un échec, c’est le signal qu’il faut ressortir vos outils
                (respiration nasale calme, expiration douce, pause détendue). Avec le temps,
                les retours deviennent plus courts, plus légers, et de plus en plus rares.
              </p>
            </FaqItem>
            <FaqItem index={7} q="Cette méthode peut-elle m’aider contre l’anxiété ?">
              <p>
                La respiration est intimement liée au système nerveux : un schéma calme envoie
                au cerveau un signal de sécurité et apaise la réponse au stress. La rééducation
                respiratoire ne remplace pas une prise en charge psychologique si elle est
                nécessaire, mais elle est une alliée puissante pour réduire la tension,
                l’anxiété et l’hypervigilance liées à la respiration.
              </p>
            </FaqItem>
          </div>
        </div>
      </section>

      {/* ================== 11. Urgences (144) ================== */}
      <section className="py-16" style={{ backgroundColor: C.cloud }}>
        <div className="max-w-4xl mx-auto px-6">
          <SectionLabel number="11">Sécurité</SectionLabel>
          <SectionTitle>Quand consulter et quand appeler les urgences</SectionTitle>

          <div
            className="rounded-2xl p-6 sm:p-8 mt-6 mb-8"
            style={{
              backgroundColor: C.dangerBg,
              border: `1px solid ${C.dangerBorder}`,
            }}
          >
            <div className="flex items-center gap-3 mb-5">
              <div
                className="w-11 h-11 rounded-full flex items-center justify-center"
                style={{ backgroundColor: C.danger, color: C.white }}
              >
                <Phone size={20} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.2em]" style={{ color: C.danger, fontWeight: 600 }}>
                  ⚠ Urgence vitale
                </p>
                <p
                  className="text-2xl sm:text-3xl"
                  style={{
                    fontFamily: 'Outfit, sans-serif',
                    fontWeight: 400,
                    color: C.danger,
                  }}
                >
                  Appelez immédiatement les urgences (144) en cas de :
                </p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                ['Douleur', 'Douleur thoracique brutale, intense ou qui s’étend (bras, mâchoire, dos)'],
                ['Souffle', 'Essoufflement soudain et sévère, impossibilité de parler'],
                ['Malaise', 'Évanouissement, malaise, perte de connaissance'],
                ['Couleur', 'Lèvres, visage ou doigts bleutés (cyanose)'],
              ].map(([k, v]) => (
                <div
                  key={k}
                  className="rounded-xl p-4 flex gap-3"
                  style={{ backgroundColor: C.white, border: `1px solid ${C.dangerBorder}` }}
                >
                  <AlertTriangle size={18} style={{ color: C.danger, flexShrink: 0, marginTop: 2 }} />
                  <div>
                    <p className="text-xs font-semibold mb-1" style={{ color: C.danger }}>
                      {k}
                    </p>
                    <p className="text-sm" style={{ color: C.ink }}>
                      {v}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <a
              href="tel:144"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-300 hover:shadow-lg"
              style={{ backgroundColor: C.danger, color: C.white }}
              data-testid="cta-call-144"
            >
              <Phone size={18} />
              Appeler le 144
            </a>
          </div>

          <Callout icon={Stethoscope} title="Consultez un médecin rapidement si">
            Vos symptômes sont récents, inhabituels, ou s’aggravent ; vous avez de la
            fièvre ; une perte de poids inexpliquée ; des réveils nocturnes avec suffocation ;
            des crachats teintés de sang ; ou si vous avez un terrain cardiaque, pulmonaire ou
            neurologique et que quelque chose change.
          </Callout>
          <div className="mt-4">
            <Callout icon={ClipboardList} title="À retenir">
              La respiration dysfonctionnelle est un diagnostic d’élimination qui se
              pose <em>après</em> vérification médicale. Le bilan en kinésithérapie ne se
              substitue jamais à un avis médical. Si vous n’avez pas encore consulté
              votre médecin, c’est la première étape.
            </Callout>
          </div>
        </div>
      </section>

      {/* ================== Final CTA ================== */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div
            className="rounded-3xl p-8 sm:p-12 text-center"
            style={{
              background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
              color: C.white,
            }}
          >
            <h3
              className="text-3xl sm:text-4xl mb-4"
              style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300 }}
            >
              Prêt à reprogrammer votre respiration&nbsp;?
            </h3>
            <p className="text-lg mb-8 opacity-90 max-w-2xl mx-auto">
              Nos exercices guidés vous accompagnent pas à pas, avec un suivi de vos progrès
              et des défis pour mesurer votre évolution.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <button
                onClick={() => navigate('/respiration')}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-base font-medium transition-all duration-300 hover:-translate-y-1"
                style={{ backgroundColor: C.white, color: C.primaryDark }}
                data-testid="cta-start-program"
              >
                Démarrer le programme
                <ArrowRight size={18} />
              </button>
              <Link
                to="/evaluer-respiration"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-base font-medium transition-all duration-300"
                style={{
                  backgroundColor: 'transparent',
                  color: C.white,
                  border: `1.5px solid rgba(255,255,255,0.5)`,
                }}
                data-testid="cta-evaluate"
              >
                Évaluer ma respiration
              </Link>
            </div>
          </div>
        </div>
      </section>

      <SiteFooterV2 />
    </div>
  );
};

export default DysfunctionalBreathingPage;
